import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PanResponder,
  Platform,
  View,
  useWindowDimensions,
} from 'react-native';
import { createDrawerNavigator } from '@react-navigation/drawer';

import CustomDrawer, { EXPANDED_WIDTH, COLLAPSED_WIDTH } from './customDrawer';
import ManualDiscountStack from './manualDiscountStack';
import OptionStack from './optionStack';
import RoleStack from './roleStack';
import SellScreenStack from './sellScreenStack';
import SettingStack from './settingStack';
import ProductStack from './productStack';
import ModifierScreen from '../components/products/modifiers/modifierScreen';
import TaxStack from './taxStack';
import AutomaticDiscountStack from './automaticDiscountStack';
import BuyXgetYDiscountStack from './buyXgetYDiscountStatck';
import CustomerStack from './customerStack';
import CustomerGroupStack from './customerGroupStack';

import StaffStack from './staffStack';
import ZoneStack from './zoneStack';
import ProfileStack from './profileStack';
import InvitedStaff from '../components/invitedStaff/invitedStaff';
import AddEditCustomerScreen from '../components/customers/addEditCustomerScreen';
import PayXGetFreeStack from './payXGetFreeStack';
import BrandStack from './brandStack';
import OrdersStack from './ordersStack';

import { DashboardStack } from './dashboardStack';
import InventoryStack from './inventoryStack';
import CategoryStack from './categoryStack';

import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { loginState, setDisplaySettings, setDrawerMenuList } from '../redux/slices/commonSlice';
import { IconType } from 'zen-core-elements';
import ImageGalleryStack from './imageGalleryStack';
import { useActivityReset } from '../platform/root/useActivityReset';
type MenuItem = {
  menuName: string;
  navigation: string;
  iconName: string;
  iconType?: IconType;
  iconSize?: number;
  subMenu?: SubMenuItem[];
  isRead?: boolean;
  isWrite?: boolean;
};

type SubMenuItem = {
  menuName: string;
  navigation: string;
  isRead?: boolean;
  isWrite?: boolean;
};

const menuList: MenuItem[] = [
  { menuName: 'Dashboard', navigation: 'DashboardStack', iconName: 'dashboard', iconType: 'material', iconSize: 23, subMenu: [] },
  { menuName: 'Sell Screen', navigation: 'SellScreenStack', iconName: 'cart-outline', iconType: 'material-community', iconSize: 23, subMenu: [] },
  {
    menuName: 'Products', navigation: 'ProductStack', iconName: 'shopping-outline', iconType: 'material-community', iconSize: 23, subMenu: [
      { menuName: 'All Products', navigation: 'ProductStack' },
      { menuName: 'Categories', navigation: 'CategoryStack' },
      { menuName: "Brands", navigation: 'BrandStack' },
      { menuName: "Inventory", navigation: 'InventoryStack' },
      { menuName: "Options", navigation: 'OptionStack' },
      { menuName: "Modifiers", navigation: 'ModifierScreen' },
      { menuName: "Image Gallery", navigation: 'ImageGalleryStack' },
    ]
  },
  { menuName: 'orders', navigation: 'OrdersStack', iconName: 'receipt-outline', iconType: 'ionicon', iconSize: 23, subMenu: [] },

  {
    menuName: 'User Management', navigation: 'CustomerStack', iconName: 'account-outline', iconType: 'material-community', iconSize: 30, subMenu: [
      { menuName: 'Customers', navigation: 'CustomerStack' },
      { menuName: 'Customer Groups', navigation: 'CustomerGroupStack' },
      { menuName: "Staff", navigation: 'StaffStack' },
      { menuName: "Invited Staff", navigation: 'InvitedStaff' },
      { menuName: "Roles", navigation: 'RoleStack' },
    ]
  },
  {
    menuName: 'Discount', navigation: 'ManualDiscountStack', iconName: 'tag-text-outline', iconType: 'material-community', iconSize: 23, subMenu: [
      { menuName: 'Manual Discounts', navigation: 'ManualDiscountStack' },
      { menuName: 'Automatic Discounts', navigation: 'AutomaticDiscountStack' },
      { menuName: 'Buy X Get Y Discounts', navigation: 'BuyXgetYDiscountStack' },
      { menuName: 'Pay X Get Free', navigation: 'PayXGetFreeStack' },
    ]
  },
  { menuName: 'Taxes', navigation: 'TaxStack', iconName: 'currency-usd', iconType: 'material-community', iconSize: 25, subMenu: [] },

  { menuName: 'Settings', navigation: 'SettingStack', iconName: 'cog-outline', iconType: 'material-community', iconSize: 23, subMenu: [] },
];


// ─── Permission-based menu filtering ────────────────────────────────────────
type ApiPermission = {
  code: string;
  actionMenuName: string;
  permissionId?: number;
  isRead?: boolean;
  isWrite?: boolean;
};
type ApiSubMenu = {
  menuId: number;
  menuName: string;
  menuIcon: string | null;
  permissions: ApiPermission[];
};
type ApiMenu = {
  menuId: number;
  menuName: string;
  menuIcon: string;
  permissions: ApiPermission[];
  subMenus: ApiSubMenu[];
};

function hasAccess(permissions: ApiPermission[] | null | undefined): boolean {
  if (!permissions || permissions.length === 0) return false;
  return permissions.some((p) => {
    if (!p?.code) return false;
    // If a admin role returned isRead and isWrite as undefined then give full access
    if (p?.isRead === undefined && p?.isWrite === undefined) return true;
    return !!p.isRead || !!p.isWrite;
  });
}

function getAccessFlags(permissions: ApiPermission[] | null | undefined) {
  let isRead = false;
  let isWrite = false;
  if (permissions && permissions.length > 0) {
    permissions?.forEach((p) => {
      if (p?.code) {
        // If a admin role returned isRead and isWrite as undefined then give full access
        if (p?.isRead === undefined && p?.isWrite === undefined) {
          isRead = true;
          isWrite = true;
        } else {
          if (p?.isRead) isRead = true;
          if (p?.isWrite) isWrite = true;
        }
      }
    });
  }
  return { isRead, isWrite };
}

/**
 * True when the role carries any real permission entry at all. A role that
 * returns no permissions (owner / unrestricted) gets full access.
 */
function hasAnyPermission(apiMenus: ApiMenu[]): boolean {
  return apiMenus.some(
    (m) =>
      hasAccess(m.permissions) ||
      (m.subMenus && m.subMenus.some((s) => hasAccess(s?.permissions)))
  );
}

/**
 * Access to the My Account area (drawer footer and ProfileStack), resolved by
 * the same rules as getFilteredMenuList so the footer and the menu agree:
 *  - no menus, or no permissions anywhere (owner / unrestricted) -> full access
 *  - no "My Account" entry in the role -> full access
 *  - otherwise the entry's own permissions *or* any of its sub menus'
 *    (Account Settings, Activity Logs, ...), where they are usually set
 */
export function getMyAccountPermission(apiMenus: ApiMenu[] | null | undefined) {
  const fullAccess = { isRead: true, isWrite: true };
  if (!apiMenus || apiMenus.length === 0 || !hasAnyPermission(apiMenus)) return fullAccess;

  const myAccount = apiMenus.find((m) => m?.menuName?.toLowerCase()?.trim() == 'my account');
  if (!myAccount) return fullAccess;

  const own = getAccessFlags(myAccount.permissions);
  const subs = (myAccount.subMenus ?? []).map((s) => getAccessFlags(s?.permissions));
  return {
    isRead: own.isRead || subs.some((s) => s.isRead),
    isWrite: own.isWrite || subs.some((s) => s.isWrite),
  };
}

/**
 * Returns a filtered version of the static `menuList` based on API permissions.
 *
 * Rules:
 *  - If a menu has NO subMenus (leaf): show it only when its `permissions` array
 *    contains at least one entry with a `code` and either isRead or isWrite is true.
 *  - If a menu HAS subMenus: show the parent when at least one subMenu has a
 *    `permissions` entry with access; filter subMenu items to only those
 *    that have access.
 *  - If `apiMenus` is null/undefined/empty, return the full `menuList` unchanged
 *    (fallback for owners / no-restriction scenarios).
 */
export function getFilteredMenuList(apiMenus: ApiMenu[] | null | undefined, dispatch: any): MenuItem[] {

  const fullAccessMenuList = menuList.map((item) => ({
    ...item,
    isRead: true,
    isWrite: true,
    subMenu: item.subMenu?.map((sub) => ({
      ...sub,
      isRead: true,
      isWrite: true,
    })),
  }));

  if (!apiMenus || apiMenus.length === 0) return fullAccessMenuList;

  if (!hasAnyPermission(apiMenus)) return fullAccessMenuList;

  // Build a fast lookup: menuName (lowercase) -> ApiMenu
  const apiMenuMap = new Map<string, ApiMenu>();
  apiMenus?.forEach((m) => {
    if (m?.menuName) {
      apiMenuMap.set(m?.menuName.toLowerCase().trim(), m);
    }
  });

  const result: MenuItem[] = [];

  for (const staticMenu of menuList) {
    const apiMenu = apiMenuMap?.get(staticMenu?.menuName?.toLowerCase()?.trim());

    // Menu not found in API response → hide it
    if (!apiMenu) continue;

    const hasSubMenus = staticMenu?.subMenu && staticMenu?.subMenu?.length > 0;

    if (!hasSubMenus) {
      // Leaf menu: show only if it has access
      const hasPermission = hasAccess(apiMenu?.permissions);
      if (hasPermission) {
        const flags = getAccessFlags(apiMenu?.permissions);
        result?.push({
          ...staticMenu,
          isRead: flags.isRead,
          isWrite: flags.isWrite
        });
      }
    } else {
      // Parent menu with subMenus: build lookup for API sub menus
      const apiSubMap = new Map<string, ApiSubMenu>();
      apiMenu?.subMenus?.forEach((s) => {
        if (s?.menuName) {
          apiSubMap.set(s.menuName.toLowerCase().trim(), s);
        }
      });

      // Filter static subMenu items to those whose API counterpart has access
      const filteredSubMenus: SubMenuItem[] = [];
      (staticMenu?.subMenu ?? []).forEach((staticSub) => {
        const apiSub = apiSubMap?.get(staticSub?.menuName?.toLowerCase()?.trim());
        if (apiSub && hasAccess(apiSub?.permissions)) {
          const flags = getAccessFlags(apiSub?.permissions);
          filteredSubMenus.push({
            ...staticSub,
            isRead: flags.isRead,
            isWrite: flags.isWrite
          });
        }
      });

      // Parent also shows if its own permissions has access
      const parentHasPermission = hasAccess(apiMenu?.permissions);
      const parentFlags = getAccessFlags(apiMenu?.permissions);

      if (filteredSubMenus?.length > 0) {
        // Show parent with only the allowed sub-items
        result?.push({
          ...staticMenu,
          subMenu: filteredSubMenus,
          isRead: true,
          isWrite: filteredSubMenus.some((s) => s.isWrite)
        });
      } else if (parentHasPermission) {
        // Parent has its own permission but no visible submenus → show as leaf
        result.push({
          ...staticMenu,
          subMenu: [],
          isRead: parentFlags.isRead,
          isWrite: parentFlags.isWrite
        });
      }
      // else: no permission at all → skip
    }
  }

  return result;
}

const DrawerStack = () => {
  const posUserRoleSAndPermissions = useAppSelector(state => state.common.posUserRoleSAndPermissions?.value)
  const { width } = useWindowDimensions();

  let INACTIVITY_TIMEOUT = 30000;
  const { Navigator, Screen } = createDrawerNavigator();
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displaySettings = useAppSelector(
    state => state?.common?.displaySettings?.value,
  );
  if (displaySettings?.displayData) {
    // Seconds per unit. Hours used to be 3.6e6 (milliseconds), making the lock
    // 1000x too long on Android and overflowing setTimeout (fires at once) on web.
    let minutes = 0;
    minutes =
      displaySettings.displayData.timeUnit == 'MINUTES'
        ? 60
        : 3600;
    // setTimeout's ceiling (~24.8 days); anything above fires immediately in browsers.
    INACTIVITY_TIMEOUT = Math.min(minutes * displaySettings.displayData.timeValue * 1000, 2147483647);
  }
  const drawerOption = { popToTopOnBlur: true, };

  // Function to reset inactivity timer
  const resetTimer = () => {
    dispatch(setDisplaySettings(false));
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(async () => {
      dispatch(setDisplaySettings(true));
      dispatch(loginState(false));
    }, INACTIVITY_TIMEOUT);
  };

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponderCapture: () => {
      if (displaySettings?.displayData?.isLockScreenInactive) {
        resetTimer();
      }
      return false;
    },
  });

  useEffect(() => {
    if (displaySettings?.displayData?.isLockScreenInactive) {
      resetTimer(); // Reset timer on mount
      return () => {
        if (timeoutRef.current != null) {
          clearTimeout(timeoutRef.current); // Cleanup timer on unmount
        }
      };
    }
  }, [displaySettings]);

  // Web: typing, scanning, scrolling and mouse movement also count as activity. No-op on native.
  useActivityReset(resetTimer, !!displaySettings?.displayData?.isLockScreenInactive);

  const isSidebarExpanded = useAppSelector(
    state => state?.common?.isSidebarExpanded,
  );

  // Compute the permission-filtered menu list whenever the API data changes
  const filteredMenuList = useMemo(
    () => getFilteredMenuList(posUserRoleSAndPermissions?.roles?.[0]?.menus ?? null, dispatch),
    [posUserRoleSAndPermissions]
  )
  const myAccountStackPermission = useMemo(
    () => getMyAccountPermission(posUserRoleSAndPermissions?.roles?.[0]?.menus),
    [posUserRoleSAndPermissions]
  )

  useEffect(() => {
    dispatch(setDrawerMenuList([...filteredMenuList, { menuName: "My Account", navigation: "ProfileStack", iconName: "account", isRead: myAccountStackPermission.isRead, isWrite: myAccountStackPermission.isWrite }]))
  }, [filteredMenuList, myAccountStackPermission])

  const getIntitalRouteName = () => {
    for (const item of filteredMenuList) {
      if (item?.subMenu?.length! > 0) {
        return item?.subMenu?.[0]?.navigation!;
      } else {
        return item?.navigation;
      }
    }
  }

  return (
    <View style={{ flex: 1 }} {...panResponder.panHandlers}>
      <Navigator
        drawerContent={(props) => <CustomDrawer intitalRouteName={getIntitalRouteName()!} filteredMenuList={filteredMenuList} myAccountStackPermission={myAccountStackPermission} descriptors={props?.descriptors} navigation={props?.navigation} state={props?.state as any} />}
        screenOptions={{
          drawerStyle: width >= 900 ? {
            width: isSidebarExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
            marginRight: isSidebarExpanded ? (COLLAPSED_WIDTH - EXPANDED_WIDTH) : 0, // Keeps layout footprint at 80px always
            backgroundColor: 'transparent',
            borderRightWidth: 0,
            zIndex: 1000,
            overflow: 'visible',
          } : { width: EXPANDED_WIDTH, backgroundColor: "transparent" },
          drawerType: width >= 900 ? 'permanent' : "front", // Permanent ensures it's always part of layout
          headerShown: false,
        }}
        initialRouteName={getIntitalRouteName()}
        /*
         * 15.21 - browser Back must go to the previously visited screen.
         *
         * A drawer is not a stack, so React Navigation defaults to returning
         * to the initial route rather than the previous one. On a phone that
         * is the right answer for the hardware back button and it stays that
         * way; in a browser it makes Back jump to the dashboard from
         * anywhere, which is not what a Back button means.
         */
        backBehavior={Platform.OS === 'web' ? 'history' : 'initialRoute'}
      >
        <Screen
          name="SellScreenStack"
          component={SellScreenStack}
          options={drawerOption}
        />
        <Screen
          name='DashboardStack'
          component={DashboardStack}
          options={drawerOption}
        />
        <Screen
          name="ProductStack"
          component={ProductStack}
          options={drawerOption}
        />
        <Screen
          name="OrdersStack"
          component={OrdersStack}
          options={drawerOption}
        />

        <Screen name="CategoryStack"
          component={CategoryStack}
          options={drawerOption} />

        <Screen
          name="InventoryStack"
          component={InventoryStack}
          options={drawerOption}
        />
        <Screen
          name="BrandStack"
          component={BrandStack}
          options={drawerOption}
        />
        <Screen
          name="OptionStack"
          component={OptionStack}
          options={drawerOption}
        />
        <Screen
          name="ModifierScreen"
          component={ModifierScreen}
          options={drawerOption}
        />
        <Screen
          name="ImageGalleryStack"
          component={ImageGalleryStack}
          options={drawerOption}
        />

        <Screen
          name="SettingStack"
          component={SettingStack}
          options={drawerOption}
        />
        <Screen name="TaxStack" component={TaxStack} options={drawerOption} />
        <Screen
          name="ManualDiscountStack"
          component={ManualDiscountStack}
          options={drawerOption}
        />
        <Screen
          name="AutomaticDiscountStack"
          component={AutomaticDiscountStack}
          options={drawerOption}
        />
        <Screen
          name="BuyXgetYDiscountStack"
          component={BuyXgetYDiscountStack}
          options={drawerOption}
        />
        <Screen
          name="PayXGetFreeStack"
          component={PayXGetFreeStack}
          options={drawerOption}
        />
        <Screen
          name="CustomerStack"
          component={CustomerStack}
          options={drawerOption}
        />
        <Screen
          name="CustomerGroupStack"
          component={CustomerGroupStack}
          options={drawerOption}
        />
        <Screen name="RoleStack" component={RoleStack} options={drawerOption} />

        <Screen
          name="StaffStack"
          component={StaffStack}
          options={drawerOption}
        />

        <Screen name="ZoneStack" component={ZoneStack} options={drawerOption} />

        <Screen
          name="ProfileStack"
          component={ProfileStack}
          options={drawerOption}
        />
        <Screen
          name="InvitedStaff"
          component={InvitedStaff}
          options={drawerOption}
        />
        <Screen
          name="AddEditCustomer"
          component={AddEditCustomerScreen}
          options={drawerOption}
        />
      </Navigator>
    </View >
  );
};

export default DrawerStack;